// Deterministic, section-only presentation. Never changes a Shopify media asset.
export function inspectArtwork(rgba, width, height) {
  const total=width*height;
  const white=i=>rgba[i*4+3]>=250 && Math.min(rgba[i*4],rgba[i*4+1],rgba[i*4+2])>=250 && Math.max(rgba[i*4],rgba[i*4+1],rgba[i*4+2])-Math.min(rgba[i*4],rgba[i*4+1],rgba[i*4+2])<=4;
  const transparent=i=>rgba[i*4+3]===0;
  const mask=new Uint8Array(total),queue=new Int32Array(total);
  let edge=0,light=0,clear=0;
  const edgePixel=i=>{edge++;if(white(i))light++;if(transparent(i))clear++;};
  for(let x=0;x<width;x++){edgePixel(x);edgePixel((height-1)*width+x);}
  for(let y=1;y<height-1;y++){edgePixel(y*width);edgePixel(y*width+width-1);}
  let mode='original',head=0,tail=0;
  const add=i=>{if(!mask[i] && white(i)){mask[i]=1;queue[tail++]=i;}};
  if(clear/edge>.98){mode='transparent';}
  else if(light/edge>.98){
    mode='edge-connected-white';
    for(let x=0;x<width;x++){add(x);add((height-1)*width+x);}
    for(let y=1;y<height-1;y++){add(y*width);add(y*width+width-1);}
    while(head<tail){const i=queue[head++],x=i%width,y=Math.floor(i/width);if(x>0)add(i-1);if(x<width-1)add(i+1);if(y>0)add(i-width);if(y<height-1)add(i+width);}
  } else {
    // Photographic scenes: trim ONLY wholly blank outer rows/columns.
    // Never flood into a lifestyle photograph or treat its scene as background.
    let top=0,bottom=height-1,left=0,right=width-1;
    const rowBlank=y=>{for(let x=0;x<width;x++)if(!white(y*width+x)&&!transparent(y*width+x))return false;return true;};
    const colBlank=x=>{for(let y=top;y<=bottom;y++)if(!white(y*width+x)&&!transparent(y*width+x))return false;return true;};
    while(top<bottom&&rowBlank(top))top++;
    while(bottom>top&&rowBlank(bottom))bottom--;
    while(left<right&&colBlank(left))left++;
    while(right>left&&colBlank(right))right--;
    if(top||bottom<height-1||left||right<width-1){mode='blank-margins-only';for(let y=0;y<height;y++)for(let x=0;x<width;x++)if(y<top||y>bottom||x<left||x>right)mask[y*width+x]=1;}
  }
  let minX=width,minY=height,maxX=-1,maxY=-1,retained=0;
  for(let i=0;i<total;i++)if(!mask[i]&&rgba[i*4+3]>0){const x=i%width,y=Math.floor(i/width);minX=Math.min(minX,x);maxX=Math.max(maxX,x);minY=Math.min(minY,y);maxY=Math.max(maxY,y);retained++;}
  const fraction=retained/total;
  // Refuse tiny/ambiguous remnants and subjects cut by the source photograph.
  if(maxX<minX || fraction<.015 || (mode==='edge-connected-white' && (fraction>.72 || minX===0 || minY===0 || maxX===width-1 || maxY===height-1))){return {mode:'original',mask:new Uint8Array(total),bounds:{x:0,y:0,width,height},reason:'Conservative fallback: ambiguous foreground'};}
  if(mode==='original')return {mode,mask,bounds:{x:0,y:0,width,height},reason:'Non-flat background preserved'};
  // Safety margin consists solely of original outside pixels, not product crop.
  const margin=Math.max(3,Math.round(Math.min(width,height)*.006));
  const x=Math.max(0,minX-margin),y=Math.max(0,minY-margin);
  return {mode,mask,bounds:{x,y,width:Math.min(width-1,maxX+margin)-x+1,height:Math.min(height-1,maxY+margin)-y+1},foreground:{x:minX,y:minY,width:maxX-minX+1,height:maxY-minY+1},retainedPixels:retained};
}

class FWOfferArtwork extends (typeof HTMLElement==='undefined'?class {}:HTMLElement) {
  connectedCallback(){this.prepare();}
  disconnectedCallback(){this.cancelled=true;}
  async prepare(){
    if(this.started)return;this.started=true;this.cancelled=false;
    const original=this.querySelector('img');
    if(!original||this.dataset.treatment==='original')return;
    this.dataset.artworkPending='true';
    try{
      const source=new Image();source.crossOrigin='anonymous';source.src=original.currentSrc||original.src;await source.decode();
      if(this.cancelled)return;
      // Source is requested at native resolution, capped in Liquid; no source upscaling.
      const surface=document.createElement('canvas');surface.width=source.naturalWidth;surface.height=source.naturalHeight;
      const ctx=surface.getContext('2d',{willReadFrequently:true});ctx.drawImage(source,0,0);
      const pixels=ctx.getImageData(0,0,surface.width,surface.height);
      const analysis=inspectArtwork(pixels.data,surface.width,surface.height);
      this.dataset.artworkMode=analysis.mode;
      if(analysis.mode==='original')return;
      for(let i=0;i<analysis.mask.length;i++)if(analysis.mask[i])pixels.data[i*4+3]=0;
      ctx.putImageData(pixels,0,0);
      const out=document.createElement('canvas'),b=analysis.bounds;
      out.width=b.width;out.height=b.height;
      // Cream is outside the retained source pixels. No filters/blend/recolour.
      const outCtx=out.getContext('2d');outCtx.fillStyle='#F7F4EC';outCtx.fillRect(0,0,b.width,b.height);outCtx.drawImage(surface,b.x,b.y,b.width,b.height,0,0,b.width,b.height);
      const image=new Image();image.alt=original.alt;image.className=original.className;image.width=b.width;image.height=b.height;image.src=out.toDataURL('image/png');await image.decode();
      image.style.maxWidth=b.width+'px';image.style.maxHeight=b.height+'px';
      if(this.cancelled)return;
      original.replaceWith(image);
      this.dataset.ready='true';
      this.dataset.sourceWidth=source.naturalWidth;this.dataset.sourceHeight=source.naturalHeight;
      this.dataset.foregroundBounds=JSON.stringify(analysis.foreground||analysis.bounds);
      this.dataset.outputBounds=JSON.stringify(b);
      this.dispatchEvent(new CustomEvent('fw-offer-artwork-ready',{bubbles:true}));
    }catch(error){
      // A CORS/load/memory failure keeps the genuine original image usable.
      this.dataset.artworkMode='original';this.dataset.artworkFallback='source-unavailable';
    }finally{delete this.dataset.artworkPending;}
  }
}
if(typeof window!=='undefined'&&!customElements.get('fw-offer-artwork'))customElements.define('fw-offer-artwork',FWOfferArtwork);
